import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';

/**
 * Enhanced useCallback that includes dependency comparison for better debugging
 */
export function useStableCallback<T extends (...args: any[]) => any>(
  callback: T,
  deps: React.DependencyList
): T {
  const ref = useRef<T | undefined>(undefined);
  const depsRef = useRef<React.DependencyList | undefined>(undefined);

  // Only update if dependencies have actually changed
  if (!depsRef.current || !shallowEqual(deps, depsRef.current)) {
    ref.current = callback;
    depsRef.current = deps;
  }

  return useCallback(ref.current!, deps);
}

/**
 * Memoized computation with debugging support
 */
export function useStableMemo<T>(
  factory: () => T,
  deps: React.DependencyList,
  debugName?: string
): T {
  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && debugName) {
      const startTime = performance.now();
      const result = factory();
      const endTime = performance.now();
      if (endTime - startTime > 10) {
        console.warn(`Slow memo computation for ${debugName}: ${endTime - startTime}ms`);
      }
      return result;
    }
    return factory();
  }, deps);
}

/**
 * Debounced value hook for performance optimization
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Throttled callback hook
 */
export function useThrottle<T extends (...args: any[]) => void>(
  callback: T,
  delay: number
): T {
  const throttleRef = useRef<boolean>(false);
  
  return useCallback((...args: Parameters<T>) => {
    if (!throttleRef.current) {
      callback(...args);
      throttleRef.current = true;
      setTimeout(() => {
        throttleRef.current = false;
      }, delay);
    }
  }, [callback, delay]) as T;
}

/**
 * Intersection Observer hook for lazy loading
 */
export function useIntersection(
  elementRef: React.RefObject<Element>,
  options?: IntersectionObserverInit
) {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsIntersecting(entry.isIntersecting),
      options
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [elementRef, options]);

  return isIntersecting;
}

/**
 * Shallow equality check for arrays and objects
 */
function shallowEqual(a: any, b: any): boolean {
  if (a === b) return true;
  
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => item === b[index]);
  }
  
  if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => a[key] === b[key]);
  }
  
  return false;
}

/**
 * Component render profiler for development
 */
export function useRenderProfiler(componentName: string) {
  const renderCount = useRef(0);
  const lastRenderTime = useRef<number | undefined>(undefined);

  if (process.env.NODE_ENV === 'development') {
    renderCount.current += 1;
    const now = performance.now();
    
    if (lastRenderTime.current) {
      const timeSinceLastRender = now - lastRenderTime.current;
      if (timeSinceLastRender < 16) { // Less than 16ms = potential unnecessary re-render
        console.warn(
          `${componentName} rendered ${renderCount.current} times. ` +
          `Last render was ${timeSinceLastRender.toFixed(2)}ms ago (potential unnecessary re-render)`
        );
      }
    }
    
    lastRenderTime.current = now;
  }
}

/**
 * Memory usage tracker (development only)
 */
export function useMemoryTracker(componentName: string) {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && 'memory' in performance) {
      const memory = (performance as any).memory;
      console.log(`${componentName} memory:`, {
        used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
        total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
        limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`
      });
    }
  });
}
